import React from "react";

import Alert from "veneer/Alert";
import VeneerBox from "veneer/Box";
import { IconButton } from "veneer/IconButton";
import { CloseIcon } from "veneer/SvgIcon";
import styled from "veneer/styled";
import { Colors } from "veneer/Theme";

import { Application } from "cortex/ui/src/fleet/view/application/model";
import * as ApplicationSelectors from "cortex/ui/src/redux-store/applications";
import { List } from "cortex/ui/src/ui/list";
import Notification from "cortex/ui/src/ui/notification";
import { Panel } from "cortex/ui/src/view/layout";

import { AppParameterForm, AppParameterFormProps } from "./AppParamForm";
import { PRIORITY } from "./utils";

interface AppParameterFormLayoutProps extends AppParameterFormProps {
  app: Application;
  onRemove: (appId: number) => void;
  readonly: boolean;
  version: {
    version: string;
  };
}

const RemoveButton = styled(IconButton)({
  backgroundColor: Colors.gray300,
  borderRadius: 16,
  height: 30,
  minWidth: 30,
  padding: 0,
  width: 30,
  "&:hover": {
    backgroundColor: Colors.gray400,
  },
});

const RemoveButtonContainer = styled("span")({
  display: "inline-flex",
  position: "absolute",
  right: -8,
  top: -8,
});

function collectAppErrors(
  app: { id: number },
  formErrors: Record<string, unknown>,
) {
  const appId = String(app.id);

  return Object.entries(formErrors || {})
    .filter(([path]) => path === appId)
    .reduce<string[]>((acc, [, value]) => {
      if (Array.isArray(value)) {
        acc.push(
          ...value.filter((item): item is string => typeof item === "string"),
        );
      } else if (typeof value === "string") {
        acc.push(value);
      }

      return acc;
    }, []);
}

const AppParameterFormLayout = ({
  app,
  formErrors,
  onRemove,
  readonly,
  priority,
  schemas,
  systemManagedParams,
  version,
  ...rest
}: AppParameterFormLayoutProps) => {
  if (!app) {
    return null;
  }

  const errors = collectAppErrors(app, formErrors);
  const errList = errors.length ? <List items={errors} /> : null;
  const objectName = priority === PRIORITY.DEVICE ? "device" : "network";
  const systemManaged = Boolean(systemManagedParams);
  const shouldDisplayAlert =
    systemManaged && ApplicationSelectors.systemManaged(app);

  return (
    <Panel
      key={app.id}
      subtitle={`Version: ${version.version}`}
      title={app.display_name}
      titleProps={{ color: Colors.blue700, variant: "h3" }}
    >
      <RemoveButtonContainer>
        <RemoveButton
          dataTestId="RemoveApp.Button"
          disabled={readonly}
          icon={<CloseIcon size="small" />}
          onClick={() => {
            onRemove(app.id);
          }}
          size="small"
          title="Remove application"
        />
      </RemoveButtonContainer>
      <Notification error={errList} />
      {shouldDisplayAlert && (
        <VeneerBox mb={2}>
          <Alert data-testid="ad-platform-sync-alert" dense severity="info">
            This {objectName} is synced with the Ad Platform, therefore certain
            fields will not be editable.
          </Alert>
        </VeneerBox>
      )}
      <AppParameterForm
        {...rest}
        app={app}
        formErrors={formErrors}
        priority={priority}
        readonly={readonly}
        schemas={schemas}
        systemManagedParams={systemManagedParams}
      />
    </Panel>
  );
};

export { AppParameterFormLayout, collectAppErrors };
